// CouncilReview.jsx — peer-review matrix + author comparison for Council v2.
//
// The matrix shows each reviewer's Borda score for each author (self excluded,
// already enforced in councilModel.rankingsToScores). The comparison list ranks
// authors by average score and expands to show each reviewer's raw review text
// (sanitized Markdown via the shared renderer).

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getAgent } from '../../lib/councilAgents.js';
import { rankAuthors } from './councilModel.js';

function shortFor(id, extraAgents) {
  return getAgent(id, extraAgents) || { id, name: id, short: (id || '?').slice(0, 2).toUpperCase(), color: 'var(--apolo-textTertiary)' };
}

export function ReviewMatrix({ selectedIds, scoresByAuthor, extraAgents = [] }) {
  const agents = selectedIds.map((id) => getAgent(id, extraAgents)).filter(Boolean);
  return (
    <div className="overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-xs">
          <thead>
            <tr className="border-b border-apolo-borderSubtle">
              <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wide text-apolo-textTertiary">Autor</th>
              {agents.map((a) => (
                <th key={a.id} className="px-3 py-2 text-center text-[10px] font-medium uppercase tracking-wide" style={{ color: a.color }} title={`Revisor: ${a.name}`}>
                  {a.short}
                </th>
              ))}
              <th className="px-3 py-2 text-center text-[10px] font-medium uppercase tracking-wide text-apolo-textTertiary">Média</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((author) => {
              const reviewMap = scoresByAuthor?.[author.id] || {};
              const scores = agents
                .filter((a) => a.id !== author.id)
                .map((rev) => reviewMap[rev.id])
                .filter((s) => typeof s === 'number');
              const avg = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : '—';
              return (
                <tr key={author.id} className="border-b border-apolo-borderSubtle last:border-0">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span aria-hidden="true" className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-apolo-bg" style={{ backgroundColor: author.color }}>
                        {author.short}
                      </span>
                      <span className="text-apolo-textSecondary">{author.name}</span>
                    </div>
                  </td>
                  {agents.map((reviewer) => {
                    const score = reviewMap[reviewer.id];
                    if (reviewer.id === author.id) {
                      return <td key={reviewer.id} className="px-3 py-2 text-center text-apolo-textTertiary" title="Autoavaliação excluída">—</td>;
                    }
                    return (
                      <td key={reviewer.id} className="px-3 py-2 text-center">
                        {typeof score === 'number' ? (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-semibold tabular-nums" style={{ color: reviewer.color, backgroundColor: `${reviewer.color}18`, border: `1px solid ${reviewer.color}40` }}>
                            {score}
                          </span>
                        ) : (
                          <span className="text-apolo-textTertiary">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center font-semibold tabular-nums text-apolo-textPrimary">{avg}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Comparison view: authors ranked by average score, with an expandable per-agent
 * review-text list. Uses `rankings` (raw) for the text and `scoresByAuthor` for
 * the order.
 */
export function ReviewComparison({ scoresByAuthor, rankings = [], extraAgents = [] }) {
  const [openId, setOpenId] = useState(null);
  const ranked = useMemo(() => rankAuthors(scoresByAuthor), [scoresByAuthor]);
  if (ranked.length === 0) return null;

  return (
    <ul className="flex flex-col gap-1.5">
      {ranked.map((entry, i) => {
        const author = shortFor(entry.agentId, extraAgents);
        const reviewsForAuthor = rankings.filter((r) => r?.parsed_ranking?.length);
        const open = openId === entry.agentId;
        return (
          <li key={entry.agentId} className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface1">
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="text-[10px] font-semibold tabular-nums text-apolo-textTertiary">#{i + 1}</span>
              <span aria-hidden="true" className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-apolo-bg" style={{ backgroundColor: author.color }}>
                {author.short}
              </span>
              <span className="flex-1 truncate text-xs text-apolo-textPrimary">{author.name}</span>
              <span className="text-[11px] tabular-nums text-apolo-textSecondary">
                {entry.avg == null ? 'sem score' : `média ${entry.avg.toFixed(1)} · ${entry.votes} voto(s)`}
              </span>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : entry.agentId)}
                aria-expanded={open}
                aria-label={`Ver críticas de ${author.name}`}
                className="text-apolo-textTertiary transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing"
              >
                {open ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
              </button>
            </div>
            {open && (
              <div className="border-t border-apolo-borderSubtle/60 px-3 py-2">
                {reviewsForAuthor.length === 0 ? (
                  <p className="text-[11px] text-apolo-textTertiary">Sem críticas registradas.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {reviewsForAuthor.map((r) => {
                      const reviewer = shortFor(r.agent_id, extraAgents);
                      return (
                        <li key={r.agent_id} className="text-[11px] leading-relaxed text-apolo-textSecondary">
                          <span className="font-medium" style={{ color: reviewer.color }}>{reviewer.name}</span>
                          <span className="text-apolo-textTertiary"> revisou {author.name}: </span>
                          <span className="text-apolo-textSecondary">
                            {(r.ranking || (r.parsed_ranking || []).join(' > ')) || '—'}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
